/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
'use client';

import { OrderStatus, type OrderDetail } from '@ahm/api-wrappers-core';
import { Button, Image, SimpleGrid, Stack, Text, rem } from '@mantine/core';
import type { ContextModalProps } from '@mantine/modals';
import dayjs from 'dayjs';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import useGetServiceDetail from '@/api/services/use-get-service-detail';
import { ModalName, showModal } from '@/components/modals';
import { useFreshchat } from '@/hooks/use-freshchat';
import { assetPrefix } from '@/lib/constants';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';

export type CancelOrderConfirmModalProps = ContextModalProps<{
  order: OrderDetail;
}>;

export function CancelOrderConfirmModal({
  context,
  id,
  innerProps: { order },
}: CancelOrderConfirmModalProps) {
  const tCancel = useTranslations('record.order_cancellation');
  const isMobile = useIsMobile();

  const { data: serviceDetails } = useGetServiceDetail(
    { service_id: order?.service._id as string, format: 'json' },
    {
      enabled: !!order?.service._id,
      queryKey: [order?.service._id, 'service-detail'],
    }
  );

  // Use the FreshChat hook
  const {
    isAvailable: isChatAvailable,
    openChat,
    isLoading: isChatLoading,
  } = useFreshchat({
    hideChatButton: true,
    tags: ['user', 'order-cancellation'],
    externalData: { orderId: order._id },
  });

  const handleCloseModal = () => {
    context.closeModal(id);
  };

  const handleNext = () => {
    context.closeModal(id);
    showModal(
      ModalName.CancelReasonList,
      { order },
      {
        centered: true,
        fullScreen: isMobile,
        withCloseButton: false,
        closeOnClickOutside: false,
        closeOnEscape: false,
        styles: {
          body: {
            background: 'white',
          },
        },
      }
    );
  };

  const isFindingDriver = useMemo(() => {
    return (
      order.status !== OrderStatus.Accepted &&
      order.status !== OrderStatus.Confirming
    );
  }, [order.status]);

  const isChatToCancel = useMemo(() => {
    if (!serviceDetails) return false;

    const currentTime = dayjs().unix();
    const acceptTime = Number(order.accept_time) || 0;
    const maxCancelDuration =
      serviceDetails?.user_cancellation_window_in_seconds?.ACCEPTED || 0;

    if (
      !acceptTime ||
      order.status !== OrderStatus.Accepted ||
      !maxCancelDuration
    ) {
      return false;
    }

    const timeSinceAccepted = currentTime - acceptTime;

    return timeSinceAccepted >= maxCancelDuration;
  }, [order?.accept_time, order.service, order.status]);

  const handleOpenChat = () => {
    openChat();
  };

  return (
    <Stack gap={rem(24)} justify="center" align="center" p={rem(24)}>
      <Image
        src={`${assetPrefix}/static/images/cancel-order/${isFindingDriver ? 'cancel-finding-driver.webp' : 'cancel-found-driver.webp'}`}
        mah={rem(160)}
        mih={rem(160)}
        style={{
          objectFit: 'contain',
        }}
      />
      {isChatToCancel ? (
        <Stack gap={rem(8)} align="center">
          <Text fz={rem(20)} lh={rem(32)} fw={700}>
            {tCancel('chat_to_cancel.title')}
          </Text>
          <Text fz={rem(16)} lh={rem(24)} fw={500} ta="center">
            {tCancel('chat_to_cancel.desc')}
          </Text>
        </Stack>
      ) : (
        <Stack gap={rem(8)} align="center">
          <Text fz={rem(20)} lh={rem(32)} fw={700}>
            {isFindingDriver
              ? tCancel('no_driver.title')
              : tCancel('had_driver.title')}
          </Text>
          <Text fz={rem(16)} lh={rem(24)} fw={500} ta="center">
            {isFindingDriver
              ? tCancel('no_driver.desc')
              : tCancel('had_driver.desc')}
          </Text>
        </Stack>
      )}

      <SimpleGrid cols={2} spacing="sm" w="100%">
        <Button
          variant="light"
          fullWidth
          h={rem(48)}
          fw={700}
          {...(isChatToCancel && {
            bg: 'neutral.3',
            c: 'neutral.7',
            styles: {
              root: {
                '&:hover': {
                  backgroundColor: 'var(--mantine-color-neutral-4)',
                },
              },
            },
          })}
          onClick={handleCloseModal}
        >
          {isFindingDriver
            ? tCancel('no_driver.keep_btn')
            : tCancel('had_driver.keep_btn')}
        </Button>

        {isChatToCancel ? (
          <Button
            fullWidth
            h={rem(48)}
            fw={700}
            variant="light"
            onClick={handleOpenChat}
            disabled={!isChatAvailable || isChatLoading}
            loading={isChatLoading}
          >
            {tCancel('chat_to_cancel.contact_btn')}
          </Button>
        ) : (
          <Button
            fullWidth
            h={rem(48)}
            fw={700}
            bg="neutral.3"
            c="neutral.7"
            styles={{
              root: {
                '&:hover': {
                  backgroundColor: 'var(--mantine-color-neutral-4)',
                },
              },
            }}
            onClick={handleNext}
          >
            {tCancel('cancel_btn')}
          </Button>
        )}
      </SimpleGrid>
    </Stack>
  );
}
